import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import musicians from "../data/soloists.json";
import SEO from "./common/SEO";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

// Instrument category mappings
const INSTRUMENT_GROUPS = {
  strings: ["violin", "viola", "cello", "doublebass"],
  winds: ["flute", "oboe", "clarinet", "bassoon", "trumpet", "alphorn"],
  keyboard: ["piano", "organ"],
  other: ["guitar", "bandoneon", "accordion", "harmonica", "mandolin", "singers"],
};

const CATEGORIES = [
  { id: "all", label_it: "Tutti gli Strumenti", label_en: "All Instruments" },
  { id: "strings", label_it: "Archi", label_en: "Strings" },
  { id: "winds", label_it: "Fiati", label_en: "Winds" },
  { id: "keyboard", label_it: "Pianoforte & Organo", label_en: "Piano & Organ" },
  { id: "other", label_it: "Altri Strumenti & Voce", label_en: "Other & Voice" },
];

const Soloists = () => {
  const { t, i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const isEnglish = (i18n.language || "it").startsWith("en");
  const title = isEnglish ? "Guest Soloists" : "Solisti Ospiti";
  const desc = isEnglish
    ? "Internationally renowned guest soloists who have performed with the Nuova Orchestra da Camera Ferruccio Busoni."
    : "I grandi solisti di fama internazionale che si sono esibiti con la Nuova Orchestra da Camera Ferruccio Busoni.";

  // Filtered list computation
  const filteredData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const result = {};

    Object.keys(musicians).forEach((instrument) => {
      // Check category filter
      if (
        selectedCategory !== "all" &&
        !INSTRUMENT_GROUPS[selectedCategory]?.includes(instrument)
      ) {
        return;
      }

      const instrumentName = (t(`concerts.${instrument}`) || instrument).toLowerCase();
      const soloists = musicians[instrument];

      if (!query) {
        result[instrument] = soloists;
        return;
      }

      // If instrument name matches query, include all
      if (instrumentName.includes(query)) {
        result[instrument] = soloists;
        return;
      }

      // Otherwise filter soloists by query
      const matching = soloists.filter((name) =>
        name.toLowerCase().includes(query)
      );

      if (matching.length > 0) {
        result[instrument] = matching;
      }
    });

    return result;
  }, [searchQuery, selectedCategory, t]);

  const totalSoloistsCount = useMemo(() => {
    return Object.values(filteredData).reduce(
      (acc, list) => acc + list.length,
      0
    );
  }, [filteredData]);

  return (
    <>
      <SEO title={title} description={desc} />
      <section className="section subpage-section">
        <div className="container subpage-container">
          {/* Intro description */}
          <div className="text-center mb-4">
            <p className="text-muted max-w-700 mx-auto mb-0">
              {isEnglish
                ? "Celebrated virtuosos, international competition winners, and guest artists who have shared the stage with the Nuova Orchestra da Camera Ferruccio Busoni."
                : "I grandi virtuosi di fama internazionale, vincitori di prestigiosi concorsi e solisti ospiti che hanno condiviso il palco con la Nuova Orchestra Busoni."}
            </p>
          </div>

          {/* Interactive Search Bar & Filters */}
          <div className="interactive-filter-card p-4 rounded-4 shadow-sm mb-5 bg-white border">
            <div className="row g-3 align-items-center">
              <div className="col-lg-6">
                <div className="modern-search-wrapper">
                  <FontAwesomeIcon icon={faSearch} className="modern-search-icon" />
                  <input
                    type="text"
                    className="form-control modern-search-input"
                    placeholder={
                      isEnglish
                        ? "Search by artist or instrument (e.g. Accardo, Piano, Gibboni)..."
                        : "Cerca artista o strumento (es. Accardo, Violino, Gibboni)..."
                    }
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Cerca artista o strumento"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="modern-search-clear"
                      onClick={() => setSearchQuery("")}
                      aria-label="Cancella ricerca"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                </div>
              </div>

              {/* Filter Pills */}
              <div className="col-lg-6">
                <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      className={`filter-pill-btn ${
                        selectedCategory === cat.id ? "active" : ""
                      }`}
                      onClick={() => setSelectedCategory(cat.id)}
                    >
                      {isEnglish ? cat.label_en : cat.label_it}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Results count counter */}
            <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top small text-muted">
              <span>
                {isEnglish
                  ? `Showing ${totalSoloistsCount} soloists`
                  : `Visualizzazione di ${totalSoloistsCount} solisti`}
              </span>
              {(searchQuery || selectedCategory !== "all") && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-decoration-none text-muted"
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedCategory("all");
                  }}
                >
                  {isEnglish ? "Reset filters" : "Azzera filtri"}
                </button>
              )}
            </div>
          </div>

          {/* Instrument Sections Grid */}
          {Object.keys(filteredData).length > 0 ? (
            <div className="row g-4">
              {Object.keys(filteredData).map((instrument) => (
                <div key={instrument} className="col-12 col-md-6 col-xl-4">
                  <div className="soloist-card h-100 p-4 rounded-4 bg-white shadow-sm border">
                    <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
                      <h4 className="soloist-instrument-title text-capitalize mb-0">
                        {t(`concerts.${instrument}`) || instrument}
                      </h4>
                      <span className="badge-count-pill">
                        {filteredData[instrument].length}
                      </span>
                    </div>

                    <div className="d-flex flex-wrap gap-2 pt-1">
                      {filteredData[instrument].map((name, idx) => (
                        <span key={idx} className="soloist-name-chip">
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-5 bg-white rounded-4 border shadow-sm">
              <p className="text-muted mb-0 fs-5">
                {isEnglish
                  ? "No soloists found matching your search."
                  : "Nessun solista trovato per i criteri inseriti."}
              </p>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm mt-3 rounded-pill px-3"
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCategory("all");
                }}
              >
                {isEnglish ? "Clear Search" : "Mostra tutti"}
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Soloists;
